import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import moment from 'moment';
import shortid from "shortid";
import {
  LIBOR_FORWARD_CURVE,
  CAP_VOLS,
  MARKET_EXPECTATIONS_SHOCK,
  PARALLEL_SHOCK,
  STANDARD_DEVIATION_SHOCK,
  TREASURIES,
} from "../../Constants/Constants";
import {
  getAmortTestData,
  downloadAmortTestData
} from "store/state/calculators/testHarness";
import DayCount from '../../Constants/DayCount';
import InterestRateType from '../../Constants/InterestRateType';
import AmortizationType from '../../Constants/AmortizationType';
import BusinessDayAdjustmentMethods from '../../Constants/BusinessDayAdjustmentMethods';
import InterestRateRounding from '../../Constants/InterestRateRounding';
import AmortResultTable from './AmortResultTable';
import SearchCriteria from './SearchCriteria';
import {
  Grid,
} from "@material-ui/core";
import PrimaryButton from "components/Global/Buttons/PrimaryButton";
import PageWrapper from 'components/Global/PageOrganization/PageWrapper'
import AmortizationTestHarnessSearch from './AmortizationTestHarnessSearch';
import TablePaginationHelper from "../../../../utils/tablePaginationHelper";

export class AmortizationTab extends Component {
  state = {
    amortizationRate: "",
    amortizationTerm: "",
    amortizationType: AmortizationType.INTEREST_ONLY,
    baseDate: moment(),
    curve: LIBOR_FORWARD_CURVE,
    customMonthlyPrincipalReduction: "",
    dayCount: DayCount.ACTUAL_OVER_360,
    interestOnlyPeriod: 0,
    interestRateType: InterestRateType.FIXED_RATE,
    interestRate: '',
    loanAmount: '$25,000,000',
    loanTerm: 120,
    payment: "",
    shockType: MARKET_EXPECTATIONS_SHOCK,
    shockValue: 0,
    spread: "",    
    startDate: moment(),
    searchCriteriaEvents: [],
    businessDayAdjustmentMethod: BusinessDayAdjustmentMethods.PRECEDING,
    lookbackDays: 0,
    interestRateRounding: InterestRateRounding.NO_ROUNDING,
    embeddedCap:"",
    embeddedFloor:"",
    partialPayments:[]
  }

  tablePaginationHelper = new TablePaginationHelper("e52dd1f6-33a8-4e8f-bd70-84fefa95a725", 20);

  submit = () => {
    const options = { ...this.state };
    this.transformOptions(options, this.tablePaginationHelper.page, this.tablePaginationHelper.rowsPerPage);
    this.closeSearchCriteria();
    this.props.getAmortTestData(options);    
  };
  closeSearchCriteria = () => {
    let searchCriteriaEvents = [...this.state.searchCriteriaEvents];
    searchCriteriaEvents.push({type: 'CLOSE'});
    this.setState({
      searchCriteriaEvents
    })
  }

  completeSearchCriteriaEvent = () => {
    let searchCriteriaEvents = [...this.state.searchCriteriaEvents];    
    searchCriteriaEvents.pop();
    this.setState({
      searchCriteriaEvents
    })
  }

  transformOptions = options => {
    options.page = this.tablePaginationHelper.page;
    options.pageSize = this.tablePaginationHelper.rowsPerPage;
    if(options.interestRate !== null)
      options.interestRate = (+options.interestRate.replace(/[^0-9.-]+/g, "")) / 100; //strips out percent sign and casts to number
    else
      delete options.interestRate;
    if(options.loanAmount)
      options.loanAmount = +options.loanAmount.replace(/[^0-9.-]+/g, "") //strips out any non-digit character 
    if(options.spread)
      options.spread = options.spread / 10000; // bp == 1/100 * 0.01
    if(options.amortizationRate)
      options.amortizationRate = +options.amortizationRate.replace(/[^0-9.-]+/g, "") /100;
    else
      delete options.amortizationRate;
    if(options.shockType === PARALLEL_SHOCK && options.shockValue && options.shockValue !== 0)
      options.shockValue *= 0.0001;
    else if(options.shockType === MARKET_EXPECTATIONS_SHOCK)
      delete options.shockValue ;

    if(options.embeddedCap)
      options.embeddedCap = +options.embeddedCap.replace(/[^0-9.-]+/g, "") /100;
    else
      delete options.embeddedCap;

    if(options.embeddedFloor)
      options.embeddedFloor = +options.embeddedFloor.replace(/[^0-9.-]+/g, "") /100;
    else
      delete options.embeddedFloor;
    
    options.baseDate = options.baseDate.format("YYYY-MM-DD");
    options.startDate = options.startDate.format("YYYY-MM-DD");

    if(options.partialPayments.length > 0){
      options.partialPaymentParameters = options.partialPayments.map(x => {
        const payment = +x.amount.replace(/[^0-9.-]+/g, "") //strips out any non-digit character 
        const payoffDate = x.date.format("YYYY-MM-DD");
        return {
          payment,
          payoffDate
        };
      });
    }
  }
  downloadAmortTestData = () => {
    const options = { ...this.state};
    this.transformOptions(options, -1, -1);
    this.transformExportOptions(options);
    this.props.downloadAmortTestData(options);
  };

  // Ryan is there some options available to set a cap of the last row to replace with a fixed number? This is a temporary hotfix.
  transformExportOptions = options => {
    options.pageSize = 100000;
  }
  
  handleChange = ({ target: { name, value } }) => {    
    this.setState({
      [name]: value
    });

    if (name === "shockType" && value === MARKET_EXPECTATIONS_SHOCK) {
      this.setState({
        shockValue: 0
      });
    } else if (name === "curve" && value === CAP_VOLS) {
      this.setState({
        shockType: MARKET_EXPECTATIONS_SHOCK
      });
    } else if(name === "amortizationType" && value === AmortizationType.STRAIGHT_LINE) {
      //if we're setting back to straight line we need to clear out custom monthly principal reduction
      // and amortizationRate
      this.setState({
        customMonthlyPrincipalReduction: "",
        amortizationRate: ""
      });
    } else if(name === "amortizationType" && value === AmortizationType.STRAIGHT_LINE_CUSTOM_AMOUNT){
      this.setState({
        amortizationRate: ""
      });
    }
    if(name === 'interestRate'){
      this.setState({
        amortizationRate: value
      })
    }
  };

  handleDateChange = date => {
    this.setState({
      baseDate: date
    });
  };

  handleChangePage = (event, newPage) => {
    if (!event) {
      return null;
    }

    this.tablePaginationHelper.page = newPage;
    this.submit();
  };

  handleChangeRowsPerPage = event => {
    this.tablePaginationHelper.rowsPerPage = event.target.value;
    this.submit();
  };

  addPartialPayment = event => {    
    const {partialPayments} = this.state;
    partialPayments.push({id: shortid.generate(), date: moment(), amount:0.0})
    this.setState({partialPayments: partialPayments});
  }

  removePartialPayment = id => {
    const {partialPayments} = this.state;
    let filtered = partialPayments.filter(x => x.id !== id);
    this.setState({partialPayments: filtered});
  }

  handlePartialPaymentChange = (id, name, value) => {
    const {partialPayments} = this.state;
    let updated = partialPayments.map(x => {
      if(x.id === id)
        x[name] = value;
      return x;
    });
    this.setState({partialPayments: updated});
  }

  render(){
    const {searchCriteriaEvents} = this.state;
    return (
      <PageWrapper>

        <Grid container>
          <SearchCriteria
            events={searchCriteriaEvents}
            onCompleteEvent={this.completeSearchCriteriaEvent}
            downloadTestData={this.downloadAmortTestData}
            tileData={
              <AmortizationTestHarnessSearch
                submit={this.submit}
                downloadTestData={this.downloadAmortTestData}
                handleChange={this.handleChange}
                handleDateChange={this.handleDateChange}
                state={this.state}
                addPartialPayment={this.addPartialPayment}
                removePartialPayment={this.removePartialPayment}
                handlePartialPaymentChange={this.handlePartialPaymentChange}
              />
            }
          />
          <PrimaryButton
            onClick={this.downloadAmortTestData}>
            Download
          </PrimaryButton>
      </Grid>
      <AmortResultTable
        submit={this.submit}
        handleChange={this.handleChange}
        handleDateChange={this.handleDateChange}
        handleChangePage={this.handleChangePage}
        handleChangeRowsPerPage={this.handleChangeRowsPerPage}
        pageSize={this.tablePaginationHelper.rowsPerPage}
        page={this.tablePaginationHelper.page}        
      />
      </PageWrapper>
    );
  }
}

const mapStateToProps = ({ authentication, testHarness }) => ({
  authentication,
  testHarness
});

export default compose(
  connect(
    mapStateToProps,
    {    
      getAmortTestData,
      downloadAmortTestData
    }
  )
)(AmortizationTab);